/**
 * Unified client activity timeline — merges assessments, posture analyses,
 * drill plan assignments, and practitioner notes into one sorted feed.
 */

import type { Assessment, PostureAnalysis } from '@/lib/types';
import type { DrillPlanAssignment } from '@/lib/drill-planner/drill-plan-types';
import { safeToDate } from '@/lib/utils';

export type ClientActivityKind =
  | 'assessment'
  | 'posture'
  | 'program'
  | 'note';

export interface ClientActivityEvent {
  id: string;
  kind: ClientActivityKind;
  occurredAt: Date;
  title: string;
  summary?: string;
  href?: string;
  meta?: Record<string, string>;
}

export interface ClientPractitionerNote {
  id: string;
  text: string;
  createdByUid: string;
  createdByEmail?: string;
  createdByName?: string;
  createdAt: string;
  updatedAt?: string;
}

function toDate(value: unknown): Date | null {
  return safeToDate(value);
}

function compareEventsDesc(a: ClientActivityEvent, b: ClientActivityEvent): number {
  return b.occurredAt.getTime() - a.occurredAt.getTime();
}

export function buildAssessmentActivityEvents(
  clientId: string,
  assessments: Assessment[]
): ClientActivityEvent[] {
  return assessments.map((assessment) => {
    const occurredAt = toDate(assessment.createdAt) ?? new Date(0);
    return {
      id: assessment.id,
      kind: 'assessment',
      occurredAt,
      title: 'SOP assessment completed',
      summary: assessment.classification
        ? `Classification: ${String(assessment.classification)}`
        : undefined,
      href: `/clients/${clientId}/assessment/${assessment.id}`,
    };
  });
}

export function buildPostureActivityEvents(
  clientId: string,
  analyses: PostureAnalysis[]
): ClientActivityEvent[] {
  return analyses.map((analysis) => {
    const occurredAt = toDate(analysis.createdAt) ?? new Date(0);
    const views = analysis.captured ? Object.keys(analysis.captured).join(', ') : undefined;
    const pattern = analysis.classification?.pattern;
    return {
      id: analysis.id,
      kind: 'posture',
      occurredAt,
      title: 'Posture analysis captured',
      summary: [pattern, views ? `Views: ${views}` : undefined].filter(Boolean).join(' · '),
      href: `/clients/${clientId}/posture-analysis/${analysis.id}`,
    };
  });
}

export function buildProgramActivityEvents(
  clientId: string,
  assignments: DrillPlanAssignment[]
): ClientActivityEvent[] {
  return assignments.map((assignment) => {
    const occurredAt =
      toDate(assignment.createdAt) ??
      toDate((assignment as { assignedAt?: unknown }).assignedAt) ??
      new Date(0);
    return {
      id: assignment.id,
      kind: 'program',
      occurredAt,
      title: 'Drill plan assigned',
      summary: assignment.drillPlanName || assignment.drillPlanDefinitionId,
      href: `/drill-planner?clientId=${clientId}`,
    };
  });
}

export function buildNoteActivityEvents(
  clientId: string,
  notes: ClientPractitionerNote[]
): ClientActivityEvent[] {
  return notes.map((note) => {
    const occurredAt = toDate(note.createdAt) ?? new Date(0);
    const author = note.createdByName || note.createdByEmail || 'Practitioner';
    const preview =
      note.text.length > 120 ? `${note.text.slice(0, 117).trim()}…` : note.text;
    return {
      id: note.id,
      kind: 'note',
      occurredAt,
      title: 'Note added',
      summary: `${author}: ${preview}`,
      href: `/clients/${clientId}?tab=notes`,
      meta: { author },
    };
  });
}

export function mergeClientActivityEvents(
  ...groups: ClientActivityEvent[][]
): ClientActivityEvent[] {
  return groups.flat().sort(compareEventsDesc);
}

export function buildClientActivityTimeline(input: {
  clientId: string;
  assessments?: Assessment[];
  postureAnalyses?: PostureAnalysis[];
  programAssignments?: DrillPlanAssignment[];
  notes?: ClientPractitionerNote[];
}): ClientActivityEvent[] {
  const { clientId } = input;
  return mergeClientActivityEvents(
    buildAssessmentActivityEvents(clientId, input.assessments ?? []),
    buildPostureActivityEvents(clientId, input.postureAnalyses ?? []),
    buildProgramActivityEvents(clientId, input.programAssignments ?? []),
    buildNoteActivityEvents(clientId, input.notes ?? [])
  );
}
